import PlatformHelper from '@sport1/react-elements/PlatformHelper'
import { useCallback, useEffect, useState } from 'react'
import {
    AspectRatioTypes,
    ExtendedRadiusType,
    ImageSizeTypes,
    UseImageSourceProps,
} from '../BaseComponents/Image/ImageInterfaces'

const RESIZE_PLACEHOLDER = ':widthx:height'
const reshapeHostRegex = /^(https:\/\/(stage-)*?(oz|reshape)+\.sport1\.de).*/

// eslint-disable-next-line @typescript-eslint/no-var-requires
export const DEFAULT_IMAGE_URL_16_9 = require('../assets/defaultImage/fallback-image-16-9.png')
// eslint-disable-next-line @typescript-eslint/no-var-requires
export const DEFAULT_IMAGE_URL_3_2 = require('../assets/defaultImage/fallback-image-3-2.png')
// eslint-disable-next-line @typescript-eslint/no-var-requires
export const DEFAULT_IMAGE_URL_1_1 = require('../assets/defaultImage/fallback-image-1-1.png')

export const reshapeImage = (imageUrl: string, width: number, height: number): string => {
    const resizePath = `${Math.floor(width)}x${Math.floor(height)}`
    return imageUrl.replace(RESIZE_PLACEHOLDER, resizePath)
}

export const ratios = (radiusType: ExtendedRadiusType, ratio: AspectRatioTypes): number => {
    if (radiusType === 'circled' || ratio === '1:1') return 1
    if (ratio === '343:272') return 343 / 272
    if (ratio === '16:9') return 16 / 9
    if (ratio === '3:2') return 3 / 2
    return !isNaN(Number(ratio)) ? Number(ratio) : 1
}

export const factors = (size: ImageSizeTypes): number => {
    if (size === 'L') return 1
    if (size === 'M') return 0.5
    return 1 / 3
}

export const parseWidthToNumber = (width: number | string, scale = false): number => {
    const dimensions = PlatformHelper.getDimensions()
    let size = dimensions.width * (scale ? dimensions.scale : 1)
    if (typeof width === 'string' && width.indexOf('%') > 0) {
        size = size * (parseInt(width) / 100)
    }
    if (typeof width === 'string' && width.indexOf('px') > 0) {
        size = parseInt(width) * (scale ? dimensions.scale : 1)
    }
    if (typeof width === 'number') {
        size = width * (scale ? dimensions.scale : 1)
    }
    return size
}

export const getBorderRadius = (
    radiusType: ExtendedRadiusType,
    width: number
): number | undefined => {
    if (radiusType === 'rounded') {
        return 4
    } else if (radiusType === 'circled') {
        return width / 2
    }
    return undefined
}

export const getDefaultUrl = (
    aspectRatio?: AspectRatioTypes,
    radiusType?: ExtendedRadiusType
): string => {
    if (radiusType === 'circled') return DEFAULT_IMAGE_URL_1_1
    switch (aspectRatio) {
        case '3:2':
            return DEFAULT_IMAGE_URL_3_2
        case '1:1':
            return DEFAULT_IMAGE_URL_1_1
    }
    return DEFAULT_IMAGE_URL_16_9
}

export const getSize = (
    factor: number,
    width: number | string,
    breakpoints?: number[]
): number[] => {
    if (breakpoints?.length && typeof width === 'string' && width.indexOf('%') > 0) {
        return breakpoints.map(breakpoint => Math.min(1200, breakpoint) * factor)
    }
    const size = parseWidthToNumber(width, true)
    let baseSize = 200
    if (size > 600) {
        baseSize = 900
    } else if (size > 200) {
        baseSize = 600
    }

    if (typeof width === 'number' || width.indexOf('px') > 0) return [baseSize]
    return [baseSize * factor]
}

type GetUrlPropsArg0 = {
    urlImageHeights: number[]
    urlImageWidths: number[]
    imageUrl?: string
}

export const getUrl = ({
    urlImageWidths,
    urlImageHeights,
    imageUrl,
}: GetUrlPropsArg0): string | string[] | undefined => {
    if (!imageUrl || !reshapeHostRegex.test(imageUrl)) {
        return imageUrl
    } else if (imageUrl && urlImageWidths.length === urlImageHeights.length) {
        return urlImageWidths.map((urlImageWidth, index) => {
            return reshapeImage(imageUrl, urlImageWidth, urlImageHeights[index])
        })
    }
}

type GetImageRenderPropsArg0 = {
    width: string | number
    size: ImageSizeTypes
    breakpoints?: number[]
    aspectRatio: AspectRatioTypes
    radiusType: ExtendedRadiusType
    imageUrl?: string
}
type UseImageSourcePropsArg0 = Partial<GetImageRenderPropsArg0>

export const useImageSource = ({
    width = '100%',
    size = 'L',
    breakpoints,
    aspectRatio = 'ORIGINAL',
    radiusType = 'none',
    imageUrl,
}: UseImageSourcePropsArg0): [UseImageSourceProps, () => void] => {
    const [error, setError] = useState(false)
    const ratio = ratios(radiusType, aspectRatio)
    const urlImageWidths = getSize(factors(size), width, breakpoints)
    const urlImageHeights = urlImageWidths.map(urlWidth =>
        aspectRatio === 'ORIGINAL' ? 0 : urlWidth / ratio
    )
    const borderRadius =
        typeof width === 'number'
            ? getBorderRadius(radiusType, width)
            : urlImageWidths.map(urlWidth => getBorderRadius(radiusType, urlWidth))
    const defaultUrl = getDefaultUrl(aspectRatio, radiusType)
    const source = getUrl({
        urlImageWidths,
        urlImageHeights,
        imageUrl,
    })

    useEffect(() => {
        setError(false)
    }, [imageUrl])

    const onError = useCallback((): void => {
        if (!error) {
            setError(true)
        }
    }, [error])

    let newSource = source
    if (error || !source || !source.length) {
        newSource = defaultUrl
    } else if (source?.length === 1) {
        newSource = source[0]
    }

    return [
        {
            source: newSource,
            defaultUrl,
            ratio,
            borderRadius:
                Array.isArray(borderRadius) && borderRadius.length === 1
                    ? borderRadius[0]
                    : (borderRadius as number[]),
        },
        onError,
    ]
}

export function getImageSource(imageData: UseImageSourceProps): string {
    if (Array.isArray(imageData.source)) {
        return imageData.defaultUrl
    } else if (imageData.source) {
        return imageData.source
    }
    return imageData.defaultUrl
}
